import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { ScatterChart } from '../../../organisms/ScatterChart/ScatterChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type CompetitorViralityView = {
  /** `Biggest moment`, `Your best`, `Your moments`. */
  kpis: KPIMetric[];
  /** One row per bucket; a brand key holds its reach on a moment, `null` otherwise. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type CompetitorViralityCardProps = WidgetBlockProps & {
  view?: CompetitorViralityView;
  /** Opens the conversations behind a bucket (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `931:36991` — each operator's viral moments as discrete events.
 *
 * Reach, not mentions: a viral moment is a post that travelled, and a brand can
 * be mentioned ten thousand times in a week without one of them reaching
 * anybody new. Hidden from the Competitors page on the user's instruction
 * (2026-09-23); it stays available as a custom-report widget.
 */
export function CompetitorViralityCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: CompetitorViralityCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      /* The card can say *why* it is empty here, which beats a generic blank.
         Before the `cardState` spread so its error / not-connected line wins. */
      emptyLabel="No moments in this period"
      {...cardState(status, hasData, onRetry)}
      title="Competitor virality"
      helpText="Compares how strongly each selected brand’s content spreads through audience interactions."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <ScatterChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            sizeBy="value"
            height={PLOT_HEIGHT}
            legend="none"
            emptyLabel="No moments in this period"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
